import React, { useEffect, useRef, useState } from 'react';
import useDeferredRowChange from './useDeferredRowChange';

/**
 * Editable single-line text cell.
 *
 * The input owns its display value via local state so a keystroke never waits on
 * the parent's recalc pipeline, and the value is never re-set under the caret —
 * which is what previously bounced the cursor to the end. External (recalc/server)
 * values reflow only while the cell is not focused.
 *
 * cellRendererParams (merged with column.cellProps):
 *   onRowChange(rowId, field, value)
 *   getRowId(row) -> string
 *   placeholder       falls back to the column's headerName, then 'Text Box'
 *   disabled
 *   readOnly
 *   maxLength        caps the input length when supplied
 *   type             'text' | 'number'  (default 'text')
 */
function TextInputCell(params) {
  const { value, data, colDef, onRowChange, getRowId, placeholder, disabled, readOnly, error, maxLength, type = 'text' } = params;
  const rowId = getRowId ? getRowId(data) : data?.id;
  const field = colDef?.field;

  const focusedRef = useRef(false);
  const [local, setLocal] = useState(value ?? '');
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  const handleChange = (e) => {
    const nextValue = type === 'number' ? e.target.value.replace(/[^0-9.\-]/g, '') : e.target.value;
    setLocal(nextValue);
    commitChange(rowId, field, nextValue);
  };

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-cell">
        <input
          className="cag-cell-input"
          type="text"
          inputMode={type === 'number' ? 'decimal' : 'text'}
          value={local}
          placeholder={placeholder ?? colDef?.headerName ?? 'Text Box'}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onBlur={() => {
            focusedRef.current = false;
            flushChange();
            setLocal(value ?? '');
          }}
          onChange={handleChange}
        />
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default TextInputCell;
